import React, { useState, useEffect } from "react";
import { Button, Input, Select, Radio, Table, Alert, Spin } from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from "antd/es/table";
import { getColumnValues, previewRecode, getColumnStats } from "../services/dataCleaning.service";

interface ColumnDef {
  column_name: string;
  inferred_type: string;
}

interface RecodeMapping {
  from: string;
  to: string | number;
}

interface RecodeState {
  selectedColumn: string;
  operation: string;
  mappings: RecodeMapping[];
  outputMode: "new_column" | "in_place";
  outputColumnName: string;
  unmappedCount?: number;
  unmappedValues?: string[];
}

interface Props {
  columns: ColumnDef[];
  datasetId?: string;
  onApply?: (spec: RecodeState) => Promise<void>;
  loading?: boolean;
}

const RecodePanel: React.FC<Props> = ({
  columns,
  datasetId,
  onApply,
  loading = false,
}) => {
  const { t } = useTranslation("dataCleaning");

  const [state, setState] = useState<RecodeState>({
    selectedColumn: "",
    operation: "recode_values",
    mappings: [],
    outputMode: "new_column",
    outputColumnName: "",
  });

  const [mappingInput, setMappingInput] = useState({ from: "", to: "" });
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [columnValuesLoading, setColumnValuesLoading] = useState(false);
  const [columnValues, setColumnValues] = useState<Array<{ value: string; count: number }>>([]);
  const [previewData, setPreviewData] = useState<any>(null);
  const [columnStats, setColumnStats] = useState<any>(null);
  const [operationStatus, setOperationStatus] = useState<Record<string, { enabled: boolean; reason?: string }>>(
    {},
  );
  const [normalizeMethod, setNormalizeMethod] = useState<"min_max" | "z_score">("min_max");
  const [binMethod, setBinMethod] = useState<"equal_width" | "quantile">("equal_width");
  const [binCount, setBinCount] = useState(4);
  const [coerceNumeric, setCoerceNumeric] = useState(true);

  // Determine if this operation needs value mapping (recode_values, score_scale, weight_values)
  const needsMapping = ["recode_values", "score_scale", "weight_values"].includes(state.operation);
  // Operations that don't need mapping: normalize, bin_band, manage_missing
  const isNonMappingOperation = ["normalize", "bin_band", "manage_missing"].includes(state.operation);

  const operations = [
    { key: "recode_values", label: t("reviewPage.recode.operations.recode_values") },
    { key: "score_scale", label: t("reviewPage.recode.operations.score_scale") },
    { key: "weight_values", label: t("reviewPage.recode.operations.weight_values") },
    { key: "normalize", label: t("reviewPage.recode.operations.normalize") },
    { key: "bin_band", label: t("reviewPage.recode.operations.bin_band") },
    { key: "manage_missing", label: t("reviewPage.recode.operations.manage_missing") },
  ];

  const handleColumnChange = async (col: string) => {
    setState((prev) => ({ ...prev, selectedColumn: col }));
    if (!datasetId || !col) return;

    setColumnValuesLoading(true);
    try {
      // Get column stats to determine enabled operations
      const stats = await getColumnStats(datasetId, col);
      setColumnStats(stats);

      // Build operation status map
      const statusMap: Record<string, { enabled: boolean; reason?: string }> = {};
      if (stats.operations) {
        stats.operations.forEach((op: any) => {
          statusMap[op.operation] = {
            enabled: op.enabled,
            reason: op.reason,
          };
        });
      }
      setOperationStatus(statusMap);

      // Get column values and auto-populate mapping table
      const data = await getColumnValues(datasetId, col);
      setColumnValues(data.values ?? []);

      const newMappings = (data.values ?? []).map((v: { value: string; count: number }) => ({
        from: v.value,
        to: "" as any,
      }));
      setState((prev) => ({
        ...prev,
        mappings: newMappings,
        // Pre-fill output column name from stats if available
        outputColumnName:
          stats.operations?.find((op: any) => op.operation === "recode_values")
            ?.suggested_output_column ?? "",
      }));
    } catch {
      setColumnValues([]);
      setState((prev) => ({ ...prev, mappings: [] }));
      setColumnStats(null);
      setOperationStatus({});
    } finally {
      setColumnValuesLoading(false);
    }
  };

  const handleAddMapping = () => {
    const { from, to } = mappingInput;
    if (!from.trim()) return;

    const toValue =
      state.operation === "recode_values" ? parseInt(to, 10) : to;

    setState((prev) => ({
      ...prev,
      mappings: [
        ...prev.mappings,
        { from: from.trim(), to: toValue },
      ],
    }));
    setMappingInput({ from: "", to: "" });
  };

  const handleRemoveMapping = (index: number) => {
    setState((prev) => ({
      ...prev,
      mappings: prev.mappings.filter((_, i) => i !== index),
    }));
  };

  const handlePreview = async () => {
    if (!datasetId || !state.selectedColumn) return;
    setPreviewLoading(true);
    try {
      // Build definition based on operation type
      let definition: Record<string, any> = {};

      if (state.operation === "recode_values") {
        const mappingObj: Record<string, any> = {};
        state.mappings.forEach((m) => {
          if (m.to !== "" && m.to !== null && m.to !== undefined) {
            mappingObj[m.from] = m.to;
          }
        });
        definition = { mapping: mappingObj, labels: mappingObj };
      } else if (state.operation === "score_scale") {
        const scoresObj: Record<string, any> = {};
        state.mappings.forEach((m) => {
          if (m.to !== "" && m.to !== null && m.to !== undefined) {
            scoresObj[m.from] = m.to;
          }
        });
        definition = { scores: scoresObj };
      } else if (state.operation === "weight_values") {
        const weightsObj: Record<string, any> = {};
        state.mappings.forEach((m) => {
          if (m.to !== "" && m.to !== null && m.to !== undefined) {
            weightsObj[m.from] = m.to;
          }
        });
        definition = { weights: weightsObj };
      } else if (state.operation === "normalize") {
        definition = { method: normalizeMethod };
      } else if (state.operation === "bin_band") {
        definition = { method: binMethod, bins: binCount };
      } else if (state.operation === "manage_missing") {
        definition = { coerce_numeric: coerceNumeric };
      }

      const result = await previewRecode(datasetId, {
        source_column: state.selectedColumn,
        operation: state.operation,
        definition,
        offset: 0,
        limit: 20,
      });
      setPreviewData(result);
      setShowPreview(true);
    } catch {
      setShowPreview(false);
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApply = async () => {
    if (!onApply) return;
    try {
      await onApply(state);
      // Reset form on success
      setState({
        selectedColumn: "",
        operation: "recode_values",
        mappings: [],
        outputMode: "new_column",
        outputColumnName: "",
      });
    } catch (err) {
      // Error handled by parent
    }
  };

  // Check if at least one mapping has a non-empty 'to' value
  const hasFilledMappings = state.mappings.some((m) => m.to !== "" && m.to !== null && m.to !== undefined);
  const canPreview = state.selectedColumn && (needsMapping ? hasFilledMappings : true);

  const mappingColumns: ColumnsType<RecodeMapping> = [
    {
      title: t("reviewPage.recode.fromLabel"),
      dataIndex: "from",
      key: "from",
    },
    {
      title: "→",
      dataIndex: "to",
      key: "to",
      width: 80,
      render: (value, _, index) => (
        <Input
          type="number"
          value={value}
          onChange={(e) => {
            const newMappings = [...state.mappings];
            newMappings[index].to = e.target.value ? parseInt(e.target.value, 10) : ("" as any);
            setState((prev) => ({ ...prev, mappings: newMappings }));
          }}
          placeholder="Code"
          size="small"
        />
      ),
    },
    {
      title: "",
      key: "actions",
      width: 40,
      render: (_, __, index) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<CloseOutlined />}
          onClick={() => handleRemoveMapping(index)}
        />
      ),
    },
  ];

  return (
    <div className="dr-recode">
      <div className="dr-recode__header">
        <h3 className="dr-recode__title">{t("reviewPage.recode.title")}</h3>
        <p className="dr-recode__subtitle">{t("reviewPage.recode.subtitle")}</p>
      </div>
      <div className="dr-recode__section">
        {/* Column selection */}
        <div className="dr-field">
          <label className="dr-field__label">
            {t("reviewPage.recode.selectColumnLabel")}
          </label>
          <Select
            className="dr-field__select"
            placeholder={t("reviewPage.recode.selectColumnPlaceholder")}
            value={state.selectedColumn || undefined}
            onChange={handleColumnChange}
            options={columns.map((col) => ({
              label: col.column_name,
              value: col.column_name,
            }))}
          />
        </div>

        {/* Operation selection */}
        {state.selectedColumn && (
          <div className="dr-field">
            <label className="dr-field__label">
              {t("reviewPage.recode.operationLabel")}
            </label>
            <div className="dr-operation-buttons">
              {operations.map((op) => {
                const status = operationStatus[op.key] ?? { enabled: true };
                return (
                  <Button
                    key={op.key}
                    className={`dr-operation-btn ${
                      state.operation === op.key ? "dr-operation-btn--active" : ""
                    }`}
                    disabled={!status.enabled}
                    title={status.reason || ""}
                    onClick={() =>
                      setState((prev) => ({
                        ...prev,
                        operation: op.key,
                        mappings: [],
                      }))
                    }
                  >
                    {op.label}
                  </Button>
                );
              })}
            </div>
            {/* Show operation-specific warnings */}
            {state.operation &&
              operationStatus[state.operation]?.reason && (
                <Alert
                  message={operationStatus[state.operation]?.reason}
                  type="info"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
          </div>
        )}

        {/* Normalize operation settings */}
        {state.selectedColumn && state.operation === "normalize" && (
          <div className="dr-field">
            <label className="dr-field__label">Method</label>
            <Select
              value={normalizeMethod}
              onChange={setNormalizeMethod}
              options={[
                { label: "Min-Max (0-1)", value: "min_max" },
                { label: "Z-Score", value: "z_score" },
              ]}
            />
          </div>
        )}

        {/* Bin/Band operation settings */}
        {state.selectedColumn && state.operation === "bin_band" && (
          <>
            <div className="dr-field">
              <label className="dr-field__label">Method</label>
              <Select
                value={binMethod}
                onChange={setBinMethod}
                options={[
                  { label: "Equal Width", value: "equal_width" },
                  { label: "Quantile", value: "quantile" },
                ]}
              />
            </div>
            <div className="dr-field">
              <label className="dr-field__label">Number of Bins</label>
              <Input
                type="number"
                value={binCount}
                onChange={(e) => setBinCount(parseInt(e.target.value, 10) || 4)}
                min={2}
                max={20}
              />
            </div>
          </>
        )}

        {/* Manage Missing operation settings */}
        {state.selectedColumn && state.operation === "manage_missing" && (
          <div className="dr-field">
            <label className="dr-field__label">Output Type</label>
            <div style={{ display: "flex", gap: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input
                  type="radio"
                  checked={coerceNumeric === true}
                  onChange={() => setCoerceNumeric(true)}
                />
                Numeric
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input
                  type="radio"
                  checked={coerceNumeric === false}
                  onChange={() => setCoerceNumeric(false)}
                />
                Text
              </label>
            </div>
          </div>
        )}

        {/* Value mapping - ONLY for recode_values, score_scale, weight_values (NOT for normalize, bin_band, manage_missing) */}
        {state.selectedColumn && needsMapping && !isNonMappingOperation && (
          <div className="dr-field">
            <label className="dr-field__label">
              {t("reviewPage.recode.valueMapping")}
            </label>

            <div className="dr-mapping-input">
              <Input
                placeholder={t("reviewPage.recode.fromLabel")}
                value={mappingInput.from}
                onChange={(e) =>
                  setMappingInput((prev) => ({
                    ...prev,
                    from: e.target.value,
                  }))
                }
                className="dr-mapping-input__field"
              />
              <span className="dr-mapping-arrow">→</span>
              <Input
                placeholder={t("reviewPage.recode.toLabel")}
                type={state.operation === "recode_values" ? "number" : "text"}
                value={mappingInput.to}
                onChange={(e) =>
                  setMappingInput((prev) => ({
                    ...prev,
                    to: e.target.value,
                  }))
                }
                className="dr-mapping-input__field"
              />
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={handleAddMapping}
                disabled={!mappingInput.from.trim() || !mappingInput.to.toString().trim()}
              />
            </div>

            {state.mappings.length > 0 && (
              <Table<RecodeMapping>
                columns={mappingColumns}
                dataSource={state.mappings}
                pagination={false}
                size="small"
                className="dr-mapping-table"
                style={{ marginTop: 12 }}
              />
            )}
          </div>
        )}

        {/* Output mode */}
        {state.selectedColumn && (
          <div className="dr-field">
            <label className="dr-field__label">
              {t("reviewPage.recode.outputMode")}
            </label>
            <Radio.Group
              value={state.outputMode}
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  outputMode: e.target.value,
                  outputColumnName:
                    e.target.value === "in_place" ? "" : prev.outputColumnName,
                }))
              }
            >
              <Radio value="new_column">
                {t("reviewPage.recode.newColumn")}
              </Radio>
              <Radio value="in_place">
                {t("reviewPage.recode.inPlace")}
              </Radio>
            </Radio.Group>
          </div>
        )}

        {/* Output column name */}
        {state.selectedColumn && state.outputMode === "new_column" && (
          <div className="dr-field">
            <label className="dr-field__label">
              {t("reviewPage.recode.outputColumnLabel")}
            </label>
            <Input
              placeholder={`${state.selectedColumn}_coded`}
              value={state.outputColumnName}
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  outputColumnName: e.target.value,
                }))
              }
              className="dr-field__input"
            />
          </div>
        )}

        {/* Preview results */}
        {showPreview && previewData && (
          <div className="dr-preview-section" style={{ marginBottom: 16, padding: 12, backgroundColor: "#f5f5f5", borderRadius: 4 }}>
            <h4 style={{ marginTop: 0, marginBottom: 8 }}>Preview Results</h4>
            <p style={{ margin: "4px 0", fontSize: 12 }}>
              <strong>{previewData.rows_affected ?? 0}</strong> rows will be affected
              {previewData.unmapped_count > 0 && `, ${previewData.unmapped_count} unmapped`}
            </p>

            {/* Show all warnings from backend */}
            {previewData.warnings && previewData.warnings.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                {previewData.warnings.map((warning: string, i: number) => (
                  <Alert
                    key={i}
                    message={warning}
                    type="warning"
                    showIcon
                    style={{ marginBottom: 4 }}
                  />
                ))}
              </div>
            )}

            {previewData.unmapped_count > 0 && (
              <Alert
                message={`⚠️ ${previewData.unmapped_count} unmapped values`}
                description={`${previewData.unmapped_values?.slice(0, 3).join(", ")}${
                  (previewData.unmapped_values?.length ?? 0) > 3 ? ", ..." : ""
                }`}
                type="warning"
                showIcon
                style={{ marginBottom: 8 }}
              />
            )}

            {previewData.distribution && previewData.distribution.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <p style={{ marginBottom: 6, fontSize: 12, fontWeight: 500 }}>Distribution:</p>
                <div style={{ fontSize: 12, maxHeight: 120, overflow: "auto" }}>
                  {previewData.distribution.map((d: any, i: number) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                      <span><strong>{d.code}</strong>: {d.label}</span>
                      <span style={{ color: "#999" }}>×{d.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {state.selectedColumn && (
          <div className="dr-recode__actions">
            <Button
              onClick={handlePreview}
              loading={previewLoading}
              disabled={!canPreview}
            >
              {t("actions.preview")}
            </Button>
            <Button
              type="primary"
              onClick={handleApply}
              loading={loading}
              disabled={!canPreview}
            >
              {t("actions.apply")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecodePanel;
